import StarterKit from '@tiptap/starter-kit';
import { Box, Button, Flex, Icon, Input, Separator, Text, VStack } from '@chakra-ui/react';
import { Control, RichTextEditor } from '../../../components/ui/rich-text-editor';
import type { EmailTemplateDocument } from '@opanuj/shared/types';
import { FiCode, FiEdit2, FiSave } from 'react-icons/fi';
import { useEditor } from '@tiptap/react';
import { useEffect, useState } from 'react';

export type MailTemplateDetailsProps = {
  template: EmailTemplateDocument | null;
  subjectDraft: string;
  htmlDraft: string;
  saving: boolean;
  onSubjectChange: (value: string) => void;
  onHtmlChange: (value: string) => void;
  onSave: () => void;
};

const MailTemplateDetails = ({
  template,
  subjectDraft,
  htmlDraft,
  saving,
  onSubjectChange,
  onHtmlChange,
  onSave,
}: MailTemplateDetailsProps) => {
  const [view, setView] = useState<'editor' | 'code'>('editor');

  const editor = useEditor({
    extensions: [StarterKit],
    content: htmlDraft || '<p>Treść wiadomości...</p>',
    immediatelyRender: false,
    editorProps: {
      attributes: {
        style: 'min-height: 280px; padding: 0.75rem 1rem; outline: none;',
      },
    },
    onUpdate: ({ editor }) => {
      onHtmlChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const nextHtml = htmlDraft || '<p>Treść wiadomości...</p>';
    if (editor.getHTML() !== nextHtml) {
      editor.commands.setContent(nextHtml, { emitUpdate: false });
    }
  }, [editor, htmlDraft]);

  if (!template) {
    return (
      <Flex align="center" justify="center" h="full" color="gray.500">
        Brak danych do wyświetlenia.
      </Flex>
    );
  }

  const isDirty = subjectDraft !== (template.subject ?? '') || htmlDraft !== (template.html ?? '');

  return (
    <VStack align="stretch" gap={4} p={{ base: 4, lg: 5 }} h="full">
      <Flex justify="space-between" align="center" gap={3} wrap="wrap">
        <Box>
          <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wide">ID dokumentu</Text>
          <Text>{template.id}</Text>
        </Box>
        <Button
          size="sm"
          colorPalette="yellow"
          onClick={onSave}
          disabled={!isDirty || saving}
          loading={saving}
        >
          <FiSave />
          <Box as="span" ml={2}>Zapisz</Box>
        </Button>
      </Flex>

      <Separator />

      <Box>
        <Text fontSize="sm" fontWeight="semibold" mb={2}>Temat</Text>
        <Input
          value={subjectDraft}
          onChange={(event) => onSubjectChange(event.target.value)}
          placeholder="Temat wiadomości"
        />
      </Box>

      <Box flex={1} minH={0}>
        <Text fontSize="sm" fontWeight="semibold" mb={2}>HTML</Text>
        <RichTextEditor.Root editor={editor}>
          <RichTextEditor.Toolbar justify="space-between" align="center">
            <RichTextEditor.ControlGroup>
              <Control.Bold />
              <Control.Italic />
              <Control.Underline />
              <Control.Strike />
              <Control.Link />
            </RichTextEditor.ControlGroup>
            <Separator orientation="vertical" height="32px" />
            <RichTextEditor.ControlGroup>
              <Control.H1 />
              <Control.H2 />
              <Control.H3 />
              <Control.H4 />
            </RichTextEditor.ControlGroup>
            <Separator orientation="vertical" height="32px" />
            <RichTextEditor.ControlGroup>
              <Control.BulletList />
              <Control.OrderedList />
              <Control.Blockquote />
              <Control.Code />
            </RichTextEditor.ControlGroup>

            <Separator orientation="vertical" height="32px" />

            <RichTextEditor.ControlGroup ml="auto">
              <Button
                size="xs"
                variant={view === 'code' ? 'surface' : 'ghost'}
                onClick={() => setView('code')}
                aria-label="Show raw HTML"
              >
                <Icon as={FiCode} boxSize={3.5} />
              </Button>
              <Button
                size="xs"
                variant={view === 'editor' ? 'surface' : 'ghost'}
                onClick={() => setView('editor')}
                aria-label="Show editor"
              >
                <Icon as={FiEdit2} boxSize={3.5} />
              </Button>
            </RichTextEditor.ControlGroup>
          </RichTextEditor.Toolbar>

          {view === 'editor' && <RichTextEditor.Content />}

          {view === 'code' && (
            <Box minH="280px" bg="gray.50" p={4} overflowX="auto">
              <Box as="pre" m={0} whiteSpace="pre-wrap" fontFamily="mono" fontSize="sm" color="gray.800">
                {htmlDraft || '<p>Brak treści.</p>'}
              </Box>
            </Box>
          )}
        </RichTextEditor.Root>
      </Box>
    </VStack>
  );
};

export default MailTemplateDetails;
